'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import type { CityCode } from '@loci/city-editions';

import { cn } from '@/components/atoms/primitives/utils';
import { GLOBAL_NAV_ACCENT_VARS } from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import {
  useCategoryTonightCounts,
  type CategoryTonightCounts,
} from '@/hooks/useCategoryTonightCounts';

import { activeCategorySlug, buildCategoryNavEntries, isCategoryPath } from './categoryNavEntries';

/** Hover-intent delay before a fine pointer opens the panel (DES-108). */
export const CATEGORIES_HOVER_OPEN_MS = 100;
/** Grace before closing once the pointer leaves trigger + panel (DES-108). */
export const CATEGORIES_HOVER_CLOSE_MS = 160;
/** Only a real hovering pointer opens on hover; touch opens on tap. */
const FINE_HOVER_QUERY = '(hover: hover) and (pointer: fine)';
/** The panel sits 4px under the bar (DS `.o-globalnav__cats` top offset). */
const PANEL_GAP_PX = 4;

type OpenSource = 'hover' | 'pointer' | 'keyboard';

export interface CategoriesDisclosureProps {
  /** City display name for the panel eyebrow ("Tonight · Charlotte"). */
  readonly cityName: string;
  /** City whose tonight counts the panel shows. */
  readonly cityCode: CityCode;
  /**
   * Tonight counts override — Storybook / tests. `undefined` (default) fetches
   * lazily on first intent; `null` renders the ten without counts.
   */
  readonly counts?: CategoryTonightCounts | null;
  /** Pathname override — Storybook / tests. Defaults to `usePathname()`. */
  readonly pathname?: string | null;
  /**
   * Whether the Categories section is the current page (trigger
   * `aria-current="page"`). The header passes `activeNav === 'categories'`;
   * when omitted it is derived from the pathname (`/category/*`).
   */
  readonly isCurrent?: boolean;
  /** Initial open — Storybook / tests only. */
  readonly defaultOpen?: boolean;
  /** Trigger classes (the header passes its primary-link skin). */
  readonly className?: string;
  /** Extra classes when the trigger is the current section (`/category/*`). */
  readonly activeClassName?: string;
}

const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The first tabbable element after `from` in document order, outside the panel. */
function nextTabbableAfter(from: HTMLElement | null): HTMLElement | null {
  if (!from) return null;
  const all = Array.from(document.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) => !el.closest('[data-testid="city-categories-panel"]') && el.tabIndex >= 0,
  );
  const index = all.indexOf(from);
  return index === -1 ? null : (all[index + 1] ?? null);
}

function prefersHover(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(FINE_HOVER_QUERY).matches
  );
}

/**
 * CategoriesDisclosure — the city header's Categories entry (WEB-1180).
 *
 * DES-108: Categories is a **disclosure, not a route**. The trigger is a
 * `<button aria-haspopup="menu">` skinned as a primary link; it opens the ten
 * canonical `/category/<slug>` hubs (DS 1.11.0 `CategoriesDisclosure`,
 * `nav/loci-globalnav.css` `.o-globalnav__cats`). There is no "All categories"
 * row and no `/categories` directory.
 *
 * Behaviour (DS 1.11.0 `wire`):
 *  - fine hover pointer: 100ms hover-intent open; leaving trigger + panel
 *    closes after a 160ms grace;
 *  - click / tap toggles everywhere (a click on a hover-opened panel keeps it
 *    open and moves focus in, rather than snapping it shut);
 *  - `↓` / `↑` on the trigger open and focus the first / last item; `↑/↓`,
 *    `Home`/`End` cycle inside; `Esc` closes and returns focus to the trigger;
 *    `Tab` out closes.
 *
 * The panel is portaled to `<body>`: the bar's `backdrop-filter` makes it the
 * containing block for fixed descendants, which would clip the panel and its
 * scrim to the 56px bar. The scrim is transparent (the page never dims) and
 * starts under the bar so the header stays hoverable while the panel is open.
 */
export function CategoriesDisclosure({
  cityName,
  cityCode,
  counts: countsOverride,
  pathname: pathnameOverride,
  isCurrent,
  defaultOpen = false,
  className,
  activeClassName,
}: CategoriesDisclosureProps): React.ReactElement {
  const routerPathname = usePathname();
  const pathname = pathnameOverride !== undefined ? pathnameOverride : routerPathname;
  const [open, setOpen] = React.useState(defaultOpen);
  const [intent, setIntent] = React.useState(defaultOpen);
  const [mounted, setMounted] = React.useState(false);
  const [panelTop, setPanelTop] = React.useState<number | null>(null);
  // Bumped on every open request that wants focus moved in, so a request
  // against an ALREADY-open panel (click on a hover-opened panel, `↑` while
  // open) still re-runs the focus effect even though `open` did not change.
  const [focusTick, setFocusTick] = React.useState(0);
  const openSourceRef = React.useRef<OpenSource | null>(defaultOpen ? 'pointer' : null);
  const focusOnOpenRef = React.useRef<'first' | 'last' | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const panelId = React.useId();

  const fetched = useCategoryTonightCounts(cityCode, countsOverride === undefined && intent);
  const counts = countsOverride !== undefined ? countsOverride : fetched;
  const entries = React.useMemo(() => buildCategoryNavEntries(counts), [counts]);
  const currentSlug = activeCategorySlug(pathname);
  const isCurrentSection = isCurrent ?? isCategoryPath(pathname);

  React.useEffect(() => setMounted(true), []);

  const clearTimer = React.useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  React.useEffect(() => clearTimer, [clearTimer]);

  const openPanel = React.useCallback(
    (source: OpenSource, focus: 'first' | 'last' | null = null): void => {
      clearTimer();
      openSourceRef.current = source;
      focusOnOpenRef.current = focus;
      if (focus !== null) setFocusTick((tick) => tick + 1);
      setIntent(true);
      setOpen(true);
    },
    [clearTimer],
  );

  const closePanel = React.useCallback(
    (returnFocus = false): void => {
      clearTimer();
      openSourceRef.current = null;
      setOpen(false);
      // Re-arm the lazy count fetch: the next intent re-runs it, so a failed
      // request retries (a good result is memoised and never refetched).
      setIntent(false);
      if (returnFocus) triggerRef.current?.focus();
    },
    [clearTimer],
  );

  // Close when the route changes (a menu item was followed).
  const lastPathRef = React.useRef(pathname);
  React.useEffect(() => {
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname;
      closePanel();
    }
  }, [pathname, closePanel]);

  // `Esc` closes from anywhere while open — a hover-opened panel never holds
  // focus, so the trigger / menu handlers alone would miss it. Focus returns
  // to the trigger only when it was inside the disclosure; otherwise the
  // visitor's focus stays where it was.
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const active = document.activeElement;
      const focusInside = active === triggerRef.current || !!menuRef.current?.contains(active);
      closePanel(focusInside);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closePanel]);

  // Anchor under the bar; re-measure while open so a resize keeps it pinned.
  React.useLayoutEffect(() => {
    if (!open) return;
    const measure = (): void => {
      const bar = triggerRef.current?.closest('header') ?? triggerRef.current;
      if (!bar) return;
      setPanelTop(Math.round(bar.getBoundingClientRect().bottom) + PANEL_GAP_PX);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [open]);

  // Move focus in for pointer / keyboard opens. A hover open never steals focus.
  React.useEffect(() => {
    if (!open || panelTop === null) return;
    const target = focusOnOpenRef.current;
    if (target === null) return;
    focusOnOpenRef.current = null;
    const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (!items || items.length === 0) return;
    (target === 'first' ? items[0] : items[items.length - 1])?.focus();
  }, [open, panelTop, focusTick]);

  const scheduleHoverOpen = (): void => {
    setIntent(true);
    if (!prefersHover()) return;
    clearTimer();
    if (open) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      openPanel('hover');
    }, CATEGORIES_HOVER_OPEN_MS);
  };

  const scheduleHoverClose = (): void => {
    if (!prefersHover()) return;
    clearTimer();
    if (!open) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      // Keep a panel the visitor has moved focus into.
      if (menuRef.current?.contains(document.activeElement)) return;
      closePanel();
    }, CATEGORIES_HOVER_CLOSE_MS);
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    // `detail === 0` is a keyboard-synthesised click (Enter / Space).
    const viaKeyboard = event.detail === 0;
    if (open && openSourceRef.current === 'hover' && !viaKeyboard) {
      // The hover-intent timer opened it a beat before the click landed:
      // honour the click as "I want this", not as a toggle-shut.
      openPanel('pointer', 'first');
      return;
    }
    if (open) {
      closePanel();
      return;
    }
    openPanel(viaKeyboard ? 'keyboard' : 'pointer', 'first');
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (open) {
        menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
      } else {
        openPanel('keyboard', 'first');
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openPanel('keyboard', 'last');
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      closePanel(true);
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    let next: number | null = null;
    switch (event.key) {
      case 'ArrowDown':
        next = (index + 1) % items.length;
        break;
      case 'ArrowUp':
        next = (index - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      case 'Escape':
        event.preventDefault();
        closePanel(true);
        return;
      case 'Tab': {
        // The panel is portaled to the end of <body>, so the browser's own
        // Tab would leave the header. Close, then continue the header's tab
        // order from the trigger: Shift+Tab lands on the trigger, Tab on the
        // next tabbable control after it.
        event.preventDefault();
        if (event.shiftKey) {
          closePanel(true);
          return;
        }
        const next = nextTabbableAfter(triggerRef.current);
        closePanel(next === null);
        next?.focus();
        return;
      }
      default:
        return;
    }
    event.preventDefault();
    items[next]?.focus();
  };

  const panel =
    open && mounted && panelTop !== null
      ? createPortal(
          <>
            <div
              aria-hidden="true"
              data-testid="city-categories-scrim"
              className="city-cats-scrim"
              style={{ '--city-cats-top': `${panelTop}px` } as React.CSSProperties}
              onClick={() => closePanel()}
            />
            <div
              id={panelId}
              data-testid="city-categories-panel"
              className="city-cats nav-menu-sheet-surface"
              style={
                {
                  ...GLOBAL_NAV_ACCENT_VARS.city,
                  '--city-cats-top': `${panelTop}px`,
                } as React.CSSProperties
              }
              onMouseEnter={clearTimer}
              onMouseLeave={scheduleHoverClose}
            >
              <div className="city-cats__eyebrow" aria-hidden="true">
                <span>Categories</span>
                <b>Tonight · {cityName}</b>
              </div>
              <div
                ref={menuRef}
                role="menu"
                aria-label="Categories"
                className="city-cats__grid"
                onKeyDown={handleMenuKeyDown}
              >
                {entries.map((entry) => {
                  const Icon = entry.Icon;
                  const isCurrent = currentSlug === entry.slug;
                  return (
                    <Link
                      key={entry.slug}
                      role="menuitem"
                      href={entry.href}
                      aria-label={entry.ariaLabel}
                      aria-current={isCurrent ? 'page' : undefined}
                      data-dim={entry.dim ? '1' : undefined}
                      data-testid={`city-categories-item-${entry.slug}`}
                      className="city-cats__item"
                      onClick={() => closePanel()}
                    >
                      <Icon aria-hidden="true" className="city-cats__icon" strokeWidth={2} />
                      <span className="city-cats__label">{entry.label}</span>
                      <span className="city-cats__n" aria-hidden="true">
                        {entry.countText}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-current={isCurrentSection ? 'page' : undefined}
        data-active={isCurrentSection ? 'true' : undefined}
        data-testid="city-header-nav-categories"
        className={cn('city-cats-trigger', className, isCurrentSection && activeClassName)}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        onMouseEnter={scheduleHoverOpen}
        onMouseLeave={scheduleHoverClose}
        onFocus={() => setIntent(true)}
      >
        Categories
        <ChevronDown aria-hidden="true" className="city-cats-trigger__caret" strokeWidth={2.5} />
      </button>
      {panel}
    </>
  );
}
